/**
 * The class report of a lab's insights: how the lab went, where students got
 * stuck, who may need support or a closer look, and every student's line.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Sparkles, AlertTriangle } from 'lucide-react';
import { labInsights } from '../services/api';
import { ACTIVE, InsightProgress } from '../components/LabInsightsCard';
import { Chip, ConceptChip, Stat, VERDICTS, WORK_PATTERNS, errorMessage, whenOf } from '../components/LabInsightsParts';

const section = { padding: '16px 20px', display: 'grid', gap: 12 };
const heading = { fontSize: 16, margin: 0 };

function StudentLink({ labId, runId, student, children }) {
  if (!student) return <span>{children}</span>;
  return <Link to={`/labs/${labId}/insights/${runId}/${student.id}`}>{children || student.name || student.studentId}</Link>;
}

export default function LabInsights() {
  const { labId } = useParams();
  const query = useQuery({
    queryKey: ['labInsights', labId],
    queryFn: () => labInsights.get(labId),
    refetchInterval: (current) => (ACTIVE.includes(current?.state?.data?.run?.status) ? 5000 : false),
  });
  if (query.isPending) return <p role="status">Loading the lab's insights…</p>;
  if (query.isError) return <p role="alert">{errorMessage(query.error, 'Could not read the lab\'s insights.')}</p>;
  const { lab, run, students = [] } = query.data;
  const byLabel = new Map(students.map((s) => [s.label, s]));
  const named = (label) => {
    const student = byLabel.get(label);
    return <StudentLink key={label} labId={labId} runId={run?.id} student={student}>{student?.name || student?.studentId || label}</StudentLink>;
  };
  const list = (labels) => labels.flatMap((label, i) => (i ? [', ', named(label)] : [named(label)]));
  // The model writes "Student N" in its sentences too: show who that is.
  const withNames = (text) => String(text || '').split(/(Student \d+)/g)
    .map((part, i) => (i % 2 && byLabel.has(part) ? <React.Fragment key={i}>{named(part)}</React.Fragment> : part));
  const report = run?.report;
  const agg = run?.aggregates || {};
  const verdict = report?.verdict ? VERDICTS[report.verdict] : null;
  const noSummary = run?.status === 'cancelled' ? 'The analysis was stopped before the class summary: the students analysed by then are below.'
    : run?.options?.ai === false ? 'Measured without AI: the class summary needs the AI analysis.'
      : run?.error || 'The class summary could not be written; each student\'s report is ready.';

  return (
    <div style={{ display: 'grid', gap: 20, maxWidth: 1100 }}>
      <div>
        <Link className="btn btn-secondary btn-sm" to={`/labs/${labId}`}><ArrowLeft size={16} aria-hidden="true" /> Back to the lab</Link>
      </div>
      <div style={{ display: 'grid', gap: 4 }}>
        <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}><Sparkles size={24} aria-hidden="true" /> Lab insights</h1>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          {lab?.name}{run?.completedAt ? ` · analysed ${whenOf(run.completedAt)}` : ''}{run?.model ? ` with ${run.model}` : run ? ' · measurements only' : ''}
        </p>
      </div>

      {!run && <div className="card" style={section}><p style={{ margin: 0 }}>This lab has not been analysed yet. Start an analysis from the lab page.</p></div>}
      {run && ACTIVE.includes(run.status) && <div className="card" style={section}><InsightProgress run={run} /></div>}
      {run?.status === 'failed' && <div className="card" style={section}><p role="alert" style={{ margin: 0, color: 'var(--accent-danger)' }}>The analysis failed: {run.error}</p></div>}

      {run && !ACTIVE.includes(run.status) && run.status !== 'failed' && (
        <>
          <section className="card" style={section} aria-labelledby="glance">
            <h2 id="glance" style={heading}>At a glance</h2>
            {verdict && <div><Chip color={verdict.color}>{verdict.label}</Chip></div>}
            {report?.overview && <p style={{ margin: 0, lineHeight: 1.6, maxWidth: '75ch' }}>{withNames(report.overview)}</p>}
            {!report && <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{noSummary}</p>}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Stat label="students analysed" value={agg.analysed} hint={agg.noActivity ? `${agg.noActivity} with nothing recorded` : null} />
              <Stat label="median active minutes" value={agg.medianActiveMinutes} />
              <Stat label="re-ran unchanged code after a failure" value={agg.studentsRerunningUnchanged} hint="students" />
              <Stat label="had 3+ failed runs in a row" value={agg.studentsWithFailureStreaks} hint="students" />
              <Stat label="stayed 5+ minutes on an error in the editor" value={agg.studentsWithLongEditorErrors} hint="students" />
              <Stat label="reworked the same code 12+ minutes" value={agg.studentsWithReworkedCode} hint="students" />
              <Stat label="pasted large code not copied in the IDE" value={agg.studentsWithLargeOutsideInsertions} hint="students" />
            </div>
            {agg.recorders?.earlier > 0 && (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
                {agg.recorders.earlier === agg.analysed ? 'Every recording here comes' : `${agg.recorders.earlier} of ${agg.analysed} recordings come`} from
                environments started before Virtual Labs recorded Run-button runs, errors shown in the editor and screen changes
                (October 2026): for them, runs are counted from terminal commands only. Environments started since record all three.
              </p>
            )}
          </section>

          {!!report?.commonBlockages?.length && (
            <section className="card" style={section} aria-labelledby="stuck">
              <h2 id="stuck" style={heading}>Where students got stuck</h2>
              {report.commonBlockages.map((b) => (
                <article key={b.title} style={{ display: 'grid', gap: 6, paddingBottom: 12, borderBottom: '1px solid var(--border-primary)' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong>{b.title}</strong> <ConceptChip concept={b.concept} />
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{b.students.length} student{b.students.length === 1 ? '' : 's'}</span>
                  </div>
                  <p style={{ margin: 0, lineHeight: 1.6 }}>{withNames(b.description)}</p>
                  {b.teachingSuggestion && <p style={{ margin: 0, fontSize: 14 }}><strong>Next class:</strong> {withNames(b.teachingSuggestion)}</p>}
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{list(b.students)}</p>
                </article>
              ))}
            </section>
          )}

          {!!agg.errorThemes?.length && (
            <section className="card" style={section} aria-labelledby="themes">
              <h2 id="themes" style={heading}>Errors across the class</h2>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Measured from failed runs and errors shown in the editor: how many students met each kind.</p>
              <div style={{ display: 'grid', gap: 6 }}>
                {agg.errorThemes.map((t) => (
                  <div key={t.theme} style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 2fr) 3fr 48px', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 14 }}>{t.label}</span>
                    <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-tertiary)' }}>
                      <div style={{ width: `${Math.round((t.students / Math.max(1, agg.analysed)) * 100)}%`, height: '100%', borderRadius: 4, background: 'var(--accent-primary)' }} />
                    </div>
                    <span style={{ fontSize: 13, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t.students}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {!!report?.studentsNeedingSupport?.length && (
            <section className="card" style={section} aria-labelledby="support">
              <h2 id="support" style={heading}>Students who may need support</h2>
              <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6 }}>
                {report.studentsNeedingSupport.map((s) => <li key={s.student}>{named(s.student)}: {withNames(s.why)}</li>)}
              </ul>
            </section>
          )}

          {report?.integrity && (
            <section className="card" style={section} aria-labelledby="closer">
              <h2 id="closer" style={heading}>Worth a closer look</h2>
              <p style={{ margin: 0, lineHeight: 1.6 }}>{report.integrity.summary ? withNames(report.integrity.summary) : 'Nothing in the recording needs a closer look.'}</p>
              {!!report.integrity.studentsToReview?.length && <p style={{ margin: 0 }}>{list(report.integrity.studentsToReview)}</p>}
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <AlertTriangle size={14} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
                These are observations with innocent explanations, not verdicts: open a student's report to see the evidence.
              </p>
            </section>
          )}

          {!!report?.teachingSuggestions?.length && (
            <section className="card" style={section} aria-labelledby="teaching">
              <h2 id="teaching" style={heading}>For the next class</h2>
              <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6 }}>{report.teachingSuggestions.map((t) => <li key={t}>{withNames(t)}</li>)}</ul>
            </section>
          )}
        </>
      )}

      {!!students.length && (
        <section className="card" style={{ ...section, overflowX: 'auto' }} aria-labelledby="students">
          <h2 id="students" style={heading}>Students</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-secondary)', fontSize: 12 }}>
                <th scope="col" style={{ padding: 8 }}>Student</th>
                <th scope="col" style={{ padding: 8 }}>Active min</th>
                <th scope="col" style={{ padding: 8 }}>Runs (failed)</th>
                <th scope="col" style={{ padding: 8 }}>Unchanged re-runs</th>
                <th scope="col" style={{ padding: 8 }}>Worked</th>
                <th scope="col" style={{ padding: 8 }}>Main difficulty</th>
                <th scope="col" style={{ padding: 8 }}>Closer look</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr key={s.id} style={{ borderTop: '1px solid var(--border-primary)' }}>
                  <td style={{ padding: 8 }}>
                    <StudentLink labId={labId} runId={run?.id} student={s} />
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.studentId}{s.status !== 'completed' ? ` · ${s.status}` : ''}</div>
                  </td>
                  <td style={{ padding: 8, fontVariantNumeric: 'tabular-nums' }}>{s.measured?.activeMinutes ?? '—'}</td>
                  <td style={{ padding: 8, fontVariantNumeric: 'tabular-nums' }}>{s.measured ? `${s.measured.runs} (${s.measured.failedRuns})` : '—'}</td>
                  <td style={{ padding: 8, fontVariantNumeric: 'tabular-nums' }}>{s.measured?.unchangedRerunsAfterFailure ?? '—'}</td>
                  <td style={{ padding: 8 }}>{s.workPattern ? WORK_PATTERNS[s.workPattern] : '—'}</td>
                  <td style={{ padding: 8 }}>{s.blockages?.[0]?.title || (s.status === 'failed' ? s.error : '—')}</td>
                  <td style={{ padding: 8 }}>{s.integrityLevel === 'review' ? <Chip color="var(--accent-warning)">Look</Chip> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
